import {
  ExecutionStrategySnapshotStatus,
  IExecutionStrategySnapshotDto,
  IOrderStrategy,
  IRateConIntegrationConfig,
  IRateConStatusDto,
  RateConConfirmationStatus,
} from "@/shared/types"

import { parseUtcDateTime } from "./parseUtcDateTime"

export const RATE_CON_INTEGRATION_HTTP_METHODS = ["GET", "POST", "PUT", "PATCH"] as const

export type RateConIntegrationHttpMethod = (typeof RATE_CON_INTEGRATION_HTTP_METHODS)[number]

export interface IRateConIntegrationFormState {
  url: string
  method: RateConIntegrationHttpMethod
  authHeaderName: string
  authHeaderValue: string
  payloadJson: string
}

const defaultIntegrationFormState = (): IRateConIntegrationFormState => ({
  url: "",
  method: "GET",
  authHeaderName: "",
  authHeaderValue: "",
  payloadJson: "",
})

const isRateConIntegrationConfig = (value: unknown): value is IRateConIntegrationConfig =>
  typeof value === "object" &&
  value !== null &&
  "url" in value &&
  typeof (value as IRateConIntegrationConfig).url === "string"

const integrationConfigToFormState = (config: IRateConIntegrationConfig): IRateConIntegrationFormState => {
  const method = (config.method ?? "GET").toUpperCase()
  const normalizedMethod = RATE_CON_INTEGRATION_HTTP_METHODS.includes(
    method as RateConIntegrationHttpMethod,
  )
    ? (method as RateConIntegrationHttpMethod)
    : "GET"

  return {
    url: config.url ?? "",
    method: normalizedMethod,
    authHeaderName: config.authHeaderName ?? "",
    authHeaderValue: config.authHeaderValue ?? "",
    payloadJson:
      config.payload == null
        ? ""
        : typeof config.payload === "string"
          ? config.payload
          : JSON.stringify(config.payload, null, 2),
  }
}

export const parseRateConIntegrationConfig = (value: unknown): IRateConIntegrationFormState => {
  if (value == null) {
    return defaultIntegrationFormState()
  }

  if (isRateConIntegrationConfig(value)) {
    return integrationConfigToFormState(value)
  }

  if (typeof value !== "string") {
    return defaultIntegrationFormState()
  }

  const trimmed = value.trim()
  if (!trimmed) {
    return defaultIntegrationFormState()
  }

  try {
    let parsed: unknown = JSON.parse(trimmed)

    if (typeof parsed === "string") {
      const nested = parsed.trim()
      parsed = nested ? JSON.parse(nested) : null
    }

    if (!isRateConIntegrationConfig(parsed)) {
      return defaultIntegrationFormState()
    }

    return integrationConfigToFormState(parsed)
  } catch {
    return defaultIntegrationFormState()
  }
}

export const serializeRateConIntegrationConfig = (
  formState: IRateConIntegrationFormState,
): { value: string; error?: string } => {
  const url = formState.url.trim()

  if (!url) {
    return { value: "" }
  }

  const payloadJson = formState.payloadJson.trim()
  let payload: unknown | null = null

  if (payloadJson) {
    try {
      payload = JSON.parse(payloadJson)
    } catch {
      return { value: "", error: "Request payload must be valid JSON." }
    }
  }

  const config: IRateConIntegrationConfig = {
    url,
    method: formState.method || "GET",
    payload,
  }

  const authHeaderName = formState.authHeaderName.trim()
  const authHeaderValue = formState.authHeaderValue.trim()

  if (authHeaderName) {
    config.authHeaderName = authHeaderName
  }

  if (authHeaderValue) {
    config.authHeaderValue = authHeaderValue
  }

  return { value: JSON.stringify(config) }
}

export const RATE_CON_IN_PROGRESS_STATUSES: ExecutionStrategySnapshotStatus[] = [
  "WAITING_FOR_RATE_CON_REQUEST",
  "RATECON_PENDING_CONFIRMATION",
]

export const isRateConStrategyStatus = (status?: ExecutionStrategySnapshotStatus) =>
  !!status && RATE_CON_IN_PROGRESS_STATUSES.includes(status)

export const isRateConInProgress = (
  strategy?: IOrderStrategy,
  rateConStatus?: IRateConStatusDto | null,
) => {
  if (rateConStatus?.integrationEnabled && isRateConStrategyStatus(rateConStatus.status)) {
    return true
  }

  if (strategy?.integrationEnabled && isRateConStrategyStatus(strategy.strategyStatus)) {
    return true
  }

  if (
    rateConStatus?.status === "RATECON_PENDING_CONFIRMATION" &&
    rateConStatus.rateConConfirmationStatus === "PENDING"
  ) {
    return true
  }

  if (
    strategy?.strategyStatus === "RATECON_PENDING_CONFIRMATION" &&
    strategy.rateConConfirmationStatus === "PENDING"
  ) {
    return true
  }

  return false
}

export const isRateConConfirmationStage = (
  strategy?: IOrderStrategy,
  rateConStatus?: IRateConStatusDto | null,
  snapshots?: IExecutionStrategySnapshotDto[],
) =>
  isRateConInProgress(strategy, rateConStatus) ||
  (snapshots?.some(isActiveRateConSnapshot) ?? false)

export const shouldPollRateConStatus = (rateConStatus?: IRateConStatusDto | null) => {
  if (!rateConStatus) {
    return false
  }

  if (
    rateConStatus.status === "RATECON_PENDING_CONFIRMATION" &&
    rateConStatus.rateConConfirmationStatus === "PENDING"
  ) {
    if (rateConStatus.rateConConfirmationEndTime) {
      const endTimeMs = parseUtcDateTime(rateConStatus.rateConConfirmationEndTime)
      return endTimeMs !== null && endTimeMs > Date.now()
    }
    return true
  }

  return (
    rateConStatus.status === "WAITING_FOR_RATE_CON_REQUEST" ||
    rateConStatus.status === "RATECON_PENDING_CONFIRMATION"
  )
}

export const shouldFetchRateConStatus = (strategy?: IOrderStrategy) => {
  if (!strategy) {
    return true
  }

  return (
    strategy.integrationEnabled === true ||
    isRateConStrategyStatus(strategy.strategyStatus) ||
    strategy.strategyStatus === "FINISHED" ||
    strategy.strategyStatus === "CANCELLED" ||
    strategy.strategyStatus === "IN_PROGRESS" ||
    !!strategy.rateConId ||
    !!strategy.rateConSent
  )
}

export const getSnapshotStrategyType = (snapshot: IExecutionStrategySnapshotDto) =>
  snapshot.type || snapshot.strategyType

const normalizeConfirmationStatus = (status?: RateConConfirmationStatus | null) =>
  status ?? undefined

export const hasRateConActivity = (
  status?: ExecutionStrategySnapshotStatus,
  confirmationStatus?: RateConConfirmationStatus,
  opts?: { pdfAvailable?: boolean; rateConSent?: boolean; rateConId?: number },
) => {
  if (isRateConStrategyStatus(status)) {
    return true
  }

  if (confirmationStatus) {
    return true
  }

  return !!(opts?.pdfAvailable || opts?.rateConSent || opts?.rateConId)
}

export const isActiveRateConSnapshot = (snapshot: IExecutionStrategySnapshotDto) =>
  isRateConStrategyStatus(snapshot.status) ||
  (snapshot.status === "RATECON_PENDING_CONFIRMATION" && snapshot.rateConConfirmationStatus === "PENDING")

export const getRateConSnapshotsWithActivity = (snapshots?: IExecutionStrategySnapshotDto[]) =>
  snapshots?.filter((snapshot) =>
    hasRateConActivity(snapshot.status, normalizeConfirmationStatus(snapshot.rateConConfirmationStatus), {
      rateConSent: snapshot.rateConSent ?? undefined,
      rateConId: snapshot.rateConId ?? undefined,
    }),
  ) ?? []

export const isHistoricalRateConSnapshot = (snapshot: IExecutionStrategySnapshotDto) => {
  if (isActiveRateConSnapshot(snapshot)) {
    return false
  }

  if (
    !hasRateConActivity(snapshot.status, normalizeConfirmationStatus(snapshot.rateConConfirmationStatus), {
      rateConSent: snapshot.rateConSent ?? undefined,
      rateConId: snapshot.rateConId ?? undefined,
    })
  ) {
    return false
  }

  if (snapshot.status === "FINISHED" || snapshot.status === "CANCELLED") {
    return true
  }

  return (
    snapshot.rateConConfirmationStatus === "DECLINED" ||
    snapshot.rateConConfirmationStatus === "EXPIRED" ||
    snapshot.rateConConfirmationStatus === "CONFIRMED"
  )
}

export const getHistoricalRateConSnapshots = (snapshots?: IExecutionStrategySnapshotDto[]) =>
  snapshots?.filter(isHistoricalRateConSnapshot) ?? []

export const shouldShowRateConSection = (
  rateConStatus?: IRateConStatusDto | null,
  snapshots?: IExecutionStrategySnapshotDto[],
) => {
  if (getRateConSnapshotsWithActivity(snapshots).length > 0) {
    return true
  }

  if (!rateConStatus?.integrationEnabled) {
    return false
  }

  return shouldShowCurrentRateConStatus(rateConStatus)
}

export const shouldShowCurrentRateConStatus = (rateConStatus?: IRateConStatusDto | null) =>
  !!rateConStatus &&
  hasRateConActivity(rateConStatus.status, rateConStatus.rateConConfirmationStatus, {
    pdfAvailable: rateConStatus.pdfAvailable,
    rateConSent: rateConStatus.rateConSent,
    rateConId: rateConStatus.rateConId,
  })

export const executionStrategySnapshotStatusToText = (status?: ExecutionStrategySnapshotStatus) => {
  switch (status) {
    case "WAITING_FOR_RATE_CON_REQUEST":
      return "Waiting for rate confirmation request"
    case "RATECON_PENDING_CONFIRMATION":
      return "Awaiting carrier confirmation"
    case "FINISHED":
      return "Rate confirmation complete"
    case "IN_PROGRESS":
      return "In progress"
    case "CANCELLED":
      return "Cancelled"
    case "NOT_STARTED":
      return "Not started"
    default:
      return status || "-"
  }
}

export const rateConConfirmationStatusToText = (status?: RateConConfirmationStatus) => {
  switch (status) {
    case "PENDING":
      return "Awaiting carrier response"
    case "CONFIRMED":
      return "Confirmed"
    case "DECLINED":
      return "Carrier declined"
    case "EXPIRED":
      return "Response timed out"
    default:
      return status || "-"
  }
}

export const getRateConBadgeColors = (
  status?: ExecutionStrategySnapshotStatus,
  confirmationStatus?: RateConConfirmationStatus,
): { color: "system-white" | "system-black"; backgroundColor: "specials-warning" | "specials-success" | "specials-error" | "specials-info" } => {
  if (confirmationStatus === "CONFIRMED") {
    return { color: "system-white", backgroundColor: "specials-success" }
  }

  if (confirmationStatus === "DECLINED" || confirmationStatus === "EXPIRED") {
    return { color: "system-white", backgroundColor: "specials-error" }
  }

  if (status === "WAITING_FOR_RATE_CON_REQUEST" || confirmationStatus === "PENDING") {
    return { color: "system-black", backgroundColor: "specials-warning" }
  }

  return { color: "system-white", backgroundColor: "specials-info" }
}
